import { useEffect, useState } from "react";
import type {
  MapDefinition,
  PublishedSnapshot,
  SnapshotPayload,
} from "@cs2/contracts";
import { getPublicMaps, getSnapshot } from "./client.js";
import { useOptionalNotification } from "../ui/index.js";

interface CachedPublicData {
  snapshot: PublishedSnapshot;
  maps: MapDefinition[];
}

function readCache(): CachedPublicData | null {
  const raw = localStorage.getItem("cs2-last-snapshot");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CachedPublicData;
  } catch {
    return null;
  }
}

/**
 * Snapshot und Karten (Callouts) landen in einem Objekt in einem einzigen
 * `setItem`-Aufruf — sonst bricht Hard Rule 1 ("ein atomarer
 * Offline-Snapshot"), sobald genau eines von beiden beim nächsten Laden fehlt.
 */
function writeCache(data: CachedPublicData): void {
  localStorage.setItem("cs2-last-snapshot", JSON.stringify(data));
  navigator.serviceWorker.controller?.postMessage({
    type: "CACHE_SNAPSHOT",
    snapshot: data.snapshot,
  });
}

export function usePublicPayload(): {
  payload: SnapshotPayload | null;
  snapshot: PublishedSnapshot | null;
  offline: boolean;
  loading: boolean;
  refreshing: boolean;
  lastReloadAt: number | null;
  refresh: () => void;
} {
  const [snapshot, setSnapshot] = useState<PublishedSnapshot | null>(null);
  const [maps, setMaps] = useState<MapDefinition[] | null>(null);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastReloadAt, setLastReloadAt] = useState<number | null>(null);
  const [reloadRequest, setReloadRequest] = useState(0);
  const notifications = useOptionalNotification();
  useEffect(() => {
    let active = true;
    if (reloadRequest > 0) setRefreshing(true);
    void Promise.allSettled([
      getSnapshot({ cache: "no-store" }),
      getPublicMaps({ cache: "no-store" }),
    ])
      .then(([snapshotResult, mapsResult]) => {
        if (!active) return;
        const nextSnapshot =
          snapshotResult.status === "fulfilled" ? snapshotResult.value : null;
        // Live-Callouts nicht erreichbar, aber Snapshot da: dessen (ggf.
        // veraltete) Karten statt eines Totalausfalls der Quickview.
        const nextMaps =
          mapsResult.status === "fulfilled"
            ? mapsResult.value
            : (nextSnapshot?.payload.maps ?? null);
        if (nextSnapshot && nextMaps) {
          setOffline(false);
          setSnapshot(nextSnapshot);
          setMaps(nextMaps);
          setLastReloadAt(Date.now());
          writeCache({ snapshot: nextSnapshot, maps: nextMaps });
          if (reloadRequest > 0)
            notifications?.notify({
              message:
                mapsResult.status === "fulfilled"
                  ? "QUICKVIEW AKTUALISIERT"
                  : "QUICKVIEW AKTUALISIERT · CALLOUTS AUS LETZTEM STAND",
              kind: mapsResult.status === "fulfilled" ? "success" : "warning",
            });
          return;
        }
        const snapshotStatus =
          snapshotResult.status === "rejected"
            ? (snapshotResult.reason as { status?: number } | null)?.status
            : undefined;
        if (snapshotStatus === 404) {
          if (reloadRequest > 0)
            notifications?.notify({
              message: "NOCH KEINE TAKTIK VERÖFFENTLICHT",
              kind: "info",
            });
          return;
        } // server reached, no snapshot published yet
        setOffline(true);
        const cached = readCache();
        if (cached) {
          setSnapshot(cached.snapshot);
          setMaps(cached.maps);
          setLastReloadAt(Date.now());
          if (reloadRequest > 0)
            notifications?.notify({
              message: "SERVER NICHT ERREICHBAR · LOKALE VORSCHAU GELADEN",
              kind: "warning",
            });
        } else if (reloadRequest > 0) {
          notifications?.notify({
            message: "QUICKVIEW KANN NICHT AKTUALISIERT WERDEN",
            kind: "error",
          });
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      active = false;
    };
  }, [notifications, reloadRequest]);
  const payload = snapshot
    ? { ...snapshot.payload, maps: maps ?? snapshot.payload.maps }
    : null;
  return {
    payload,
    snapshot,
    offline,
    loading,
    refreshing,
    lastReloadAt,
    refresh: () => setReloadRequest((request) => request + 1),
  };
}
